import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useProductName, isPlatformHost } from "@/context/BrandContext";
import MarketingShell from "@/components/marketing/MarketingShell";
import {
  PhoneCall, GitBranch, PhoneForwarded, Voicemail, Activity, Users,
  CreditCard, ArrowRight, Check, Zap, ShieldCheck, Rocket, WandSparkles, PhoneOutgoing, Play, Pause, Headphones,
  Gauge, Bot, Volume2,
} from "lucide-react";

const C = {
  emerald: "#10b981", violet: "hsl(var(--primary))", amber: "#f59e0b",
  slate: "#64748b", indigo: "#6366f1", rose: "#f43f5e", blue: "#3b82f6",
};

const NODES = {
  start:    { x: 420, y: 24,  w: 160, h: 46, c: "emerald", t: "Start",              s: "Call initiated" },
  intro:    { x: 250, y: 120, w: 180, h: 54, c: "violet",  t: "Play Intro",         s: "AI voice greeting" },
  reprompt: { x: 570, y: 120, w: 180, h: 54, c: "violet",  t: "Play Re-prompt",     s: "“Are you still there?”" },
  wait:     { x: 250, y: 214, w: 180, h: 54, c: "amber",   t: "Wait for Customer",  s: "Voice activity / VAD" },
  silence:  { x: 570, y: 214, w: 180, h: 54, c: "slate",   t: "Silence",            s: "Max 2 attempts" },
  response: { x: 330, y: 314, w: 340, h: 60, c: "indigo",  t: "Customer Response",  s: "AI intent + sentiment router" },
  noans:    { x: 740, y: 314, w: 170, h: 54, c: "rose",    t: "Hang Up",            s: "No answer" },
  great:    { x: 140, y: 436, w: 190, h: 54, c: "violet",  t: "Play: Great!",       s: "“Connecting you now.”" },
  thanks:   { x: 405, y: 436, w: 190, h: 54, c: "violet",  t: "Play: Thanks, bye",  s: "Not interested" },
  callback: { x: 670, y: 436, w: 190, h: 54, c: "violet",  t: "Play: Callback",     s: "“We’ll call you back.”" },
  transfer: { x: 140, y: 534, w: 190, h: 54, c: "blue",    t: "Transfer to Agent",  s: "SIP warm hand-off" },
  hangup1:  { x: 405, y: 534, w: 190, h: 54, c: "rose",    t: "Hang Up",            s: "not_interested" },
  hangup2:  { x: 670, y: 534, w: 190, h: 54, c: "rose",    t: "Hang Up",            s: "callback" },
  end:      { x: 430, y: 616, w: 160, h: 46, c: "slate",   t: "End",                s: "Flow complete" },
};

const EDGES = [
  ["start", "intro"], ["start", "reprompt", "retry"],
  ["intro", "wait"], ["reprompt", "wait"],
  ["wait", "response", "speech"], ["wait", "silence", "silence"],
  ["silence", "noans", "max"],
  ["response", "great", "interested"], ["response", "thanks", "not interested"], ["response", "callback", "callback"],
  ["great", "transfer"], ["thanks", "hangup1"], ["callback", "hangup2"],
  ["transfer", "end"], ["hangup1", "end"], ["hangup2", "end"], ["noans", "end"],
];

function edgePath(a, b) {
  const sx = a.x + a.w / 2, sy = a.y + a.h;
  const tx = b.x + b.w / 2, ty = b.y;
  const dy = Math.max(30, (ty - sy) / 2);
  return `M ${sx} ${sy} C ${sx} ${sy + dy}, ${tx} ${ty - dy}, ${tx} ${ty}`;
}

const HEX = {
  emerald: "#10b981", violet: "#7c5cfc", amber: "#f59e0b",
  slate: "#64748b", indigo: "#6366f1", rose: "#f43f5e", blue: "#3b82f6",
};

function FlowDiagram() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-xl" data-testid="call-flow-diagram">
      <div className="flex h-11 items-center gap-2 border-b border-border bg-muted/50 px-4">
        <span className="h-3 w-3 rounded-full bg-rose-500" />
        <span className="h-3 w-3 rounded-full bg-amber-500" />
        <span className="h-3 w-3 rounded-full bg-emerald-500" />
        <span className="ml-3 font-mono text-xs tracking-widest text-muted-foreground">
          campaign-designer — real-estate-qualification.flow
        </span>
        <span className="ml-auto rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 font-mono text-[11px] text-primary">
          14 nodes · 17 paths
        </span>
      </div>
      <svg viewBox="0 0 1000 690" className="block h-auto w-full bg-muted/30" role="img" aria-label="AI call flow diagram">
        <defs>
          <pattern id="dots" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" className="fill-border" />
          </pattern>
        </defs>
        <rect x="0" y="0" width="1000" height="690" fill="url(#dots)" />
        {EDGES.map(([from, to, label], i) => {
          const a = NODES[from], b = NODES[to];
          const d = edgePath(a, b);
          const mx = (a.x + a.w / 2 + b.x + b.w / 2) / 2;
          const my = (a.y + a.h + b.y) / 2;
          return (
            <g key={i}>
              <path d={d} fill="none" stroke="currentColor" className="text-border" strokeWidth="2" />
              <path d={d} className="flow-edge" fill="none" stroke={HEX[b.c]} strokeWidth="2"
                strokeOpacity="0.65" style={{ animationDelay: `${(i % 8) * 0.15}s` }} />
              {label && (
                <text x={mx} y={my} textAnchor="middle" fontSize="10" fontFamily="monospace"
                  className="fill-muted-foreground">{label}</text>
              )}
            </g>
          );
        })}
        {Object.entries(NODES).map(([id, n], i) => (
          <g key={id} className="flow-node" style={{ animationDelay: `${i * 0.06}s` }}>
            <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="12"
              fill={HEX[n.c]} fillOpacity="0.12" stroke={HEX[n.c]} strokeOpacity="0.55" strokeWidth="1.5" />
            <rect x={n.x} y={n.y + 10} width="4" height={n.h - 20} rx="2" fill={HEX[n.c]} />
            <text x={n.x + 16} y={n.y + 24} fontSize="14" fontWeight="700" className="fill-foreground"
              fontFamily="Outfit, sans-serif">{n.t}</text>
            <text x={n.x + 16} y={n.y + 41} fontSize="11" className="fill-muted-foreground"
              fontFamily="'DM Sans', sans-serif">{n.s}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

const FEATURES = [
  { icon: WandSparkles, badge: "Core Studio", title: "Visual Drag-and-Drop Flow Designer",
    body: "Build multi-branch call journeys on an infinite canvas — play audio, wait for speech, capture DTMF, branch on intent, and hand off to humans. No code." },
  { icon: PhoneForwarded, badge: "Telephony", title: "Warm Human Transfer",
    body: "The moment a prospect is interested, the bot bridges the live SIP call to your agent with zero audio drops — context intact." },
  { icon: Voicemail, badge: "AI Guardrail", title: "Answering-Machine Detection",
    body: "Detects human vs voicemail in ~1 second and routes accordingly, so agents only talk to real people." },
  { icon: Activity, badge: "Control Room", title: "Live Calls & Real-Time CDRs",
    body: "Watch every active call, dispositions, and outcomes update live — with full call-detail records and transcripts." },
  { icon: Zap, badge: "Scale", title: "Concurrent-Call Scaling",
    body: "Run thousands of simultaneous conversations across a fleet of AI engines with race-safe concurrency limits per account." },
  { icon: Users, badge: "Enterprise", title: "Multi-Tenant: Admin · Reseller · Customer",
    body: "Isolated accounts, reseller sub-billing, per-channel pricing, and role-based control across the whole platform." },
];

const STEPS = [
  { n: "01", t: "Design the flow", d: "Drag nodes to script your greeting, questions, branches and hand-offs in the visual designer." },
  { n: "02", t: "Connect telephony", d: "Point your SIP trunks / VICIdial and AI engines. Set your concurrent-channel capacity." },
  { n: "03", t: "Launch & scale", d: "Dispatch the campaign. The AI qualifies leads and transfers hot prospects to agents in real time." },
];

function HeroVisual({ homeDemo, playing, onTogglePlay }) {
  return (
    <div className="relative mx-auto w-full max-w-md lg:max-w-none" data-testid="hero-visual">
      <style>{`
        @keyframes hero-wave { 0%{transform:scaleY(.35)} 50%{transform:scaleY(1)} 100%{transform:scaleY(.35)} }
        @keyframes hero-ring { 0%{transform:scale(.85);opacity:.5} 70%{transform:scale(1.25);opacity:0} 100%{opacity:0} }
        @keyframes hero-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes hero-play-ripple {
          0% { transform: scale(1); opacity: 0.55; }
          70% { transform: scale(1.85); opacity: 0; }
          100% { transform: scale(1.85); opacity: 0; }
        }
        .hero-wave{transform-box:fill-box;transform-origin:center bottom;animation:hero-wave 1.1s ease-in-out infinite}
        .hero-wave-paused{animation-play-state:paused}
        .hero-float{animation:hero-float 4.5s ease-in-out infinite}
        .hero-ring{transform-box:fill-box;transform-origin:center;animation:hero-ring 2.4s ease-out infinite}
        .hero-play-ripple{position:absolute;inset:0;border-radius:9999px;border:2px solid rgba(255,255,255,0.7);animation:hero-play-ripple 2s ease-out infinite;pointer-events:none}
      `}</style>
      {/* Float the whole composition so the play control stays locked to the phone */}
      <div className="hero-float relative">
        <svg viewBox="0 0 480 440" className="block h-auto w-full" role="img" aria-label="AI voice agent illustration">
          <defs>
            <linearGradient id="hero-bg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.16" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="hero-phone" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#f4f2ff" />
            </linearGradient>
            <linearGradient id="hero-accent" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" />
              <stop offset="100%" stopColor="#6366f1" />
            </linearGradient>
          </defs>

          <circle cx="260" cy="210" r="170" fill="url(#hero-bg)" />
          <circle cx="260" cy="210" r="128" className="hero-ring" fill="none" stroke="hsl(var(--primary))" strokeOpacity="0.28" strokeWidth="2" />
          <circle cx="260" cy="210" r="128" className="hero-ring" style={{ animationDelay: "0.8s" }} fill="none" stroke="hsl(var(--primary))" strokeOpacity="0.18" strokeWidth="2" />

          <g transform="translate(150 70)">
            <rect x="40" y="20" width="160" height="300" rx="36" fill="url(#hero-phone)" stroke="hsl(var(--border))" strokeWidth="2" />
            <rect x="52" y="44" width="136" height="210" rx="18" fill="#0f172a" />
            <circle cx="120" cy="110" r="28" fill="url(#hero-accent)" />
            <path d="M108 110c0-6.6 5.4-12 12-12s12 5.4 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M106 122c4 6 10 9 14 9s10-3 14-9" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
            {/* Waveform only when not showing the in-screen player chrome */}
            {!homeDemo && (
              <g transform="translate(78 160)" fill="hsl(var(--primary))">
                {[10, 18, 28, 22, 14, 24, 16, 12].map((h, i) => (
                  <rect
                    key={i}
                    className="hero-wave hero-wave-paused"
                    x={i * 12}
                    y={28 - h}
                    width="6"
                    height={h}
                    rx="3"
                    style={{ animationDelay: `${i * 0.08}s` }}
                  />
                ))}
              </g>
            )}
            <text x="120" y={homeDemo ? 246 : 220} textAnchor="middle" fill="#94a3b8" fontSize="11" fontFamily="ui-sans-serif, system-ui, sans-serif">Live AI Agent</text>
            <circle cx="120" cy="286" r="14" fill="#e2e8f0" />
          </g>

          <g transform="translate(28 150)">
            <rect width="132" height="56" rx="14" fill="#fff" stroke="hsl(var(--border))" strokeWidth="1.5" />
            <circle cx="28" cy="28" r="14" fill="#10b981" opacity="0.15" />
            <path d="M22 28h12M28 22v12" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round" />
            <text x="50" y="24" fill="#0f172a" fontSize="11" fontWeight="600" fontFamily="ui-sans-serif, system-ui, sans-serif">Qualified</text>
            <text x="50" y="40" fill="#64748b" fontSize="10" fontFamily="ui-sans-serif, system-ui, sans-serif">Warm transfer</text>
          </g>

          <g transform="translate(320 290)">
            <rect width="140" height="56" rx="14" fill="#fff" stroke="hsl(var(--border))" strokeWidth="1.5" />
            <circle cx="28" cy="28" r="14" fill="hsl(var(--primary))" opacity="0.15" />
            <path d="M22 32c3-8 9-12 12-12s9 4 12 12" fill="none" stroke="hsl(var(--primary))" strokeWidth="2.2" strokeLinecap="round" />
            <circle cx="28" cy="24" r="3.5" fill="hsl(var(--primary))" />
            <text x="50" y="24" fill="#0f172a" fontSize="11" fontWeight="600" fontFamily="ui-sans-serif, system-ui, sans-serif">Live agent</text>
            <text x="50" y="40" fill="#64748b" fontSize="10" fontFamily="ui-sans-serif, system-ui, sans-serif">SIP hand-off</text>
          </g>

          <path d="M300 250 C 340 250, 360 280, 380 300" fill="none" stroke="hsl(var(--primary))" strokeOpacity="0.45" strokeWidth="2" strokeDasharray="5 5" className="flow-edge" />
        </svg>

        {homeDemo && (
          /*
            Phone screen in viewBox: x=202–338 (42.1%–70.4%), y=114–254 (25.9%–57.7%).
            Player sits in the lower half of that screen, above “Live AI Agent”.
          */
          <div
            className="pointer-events-none absolute"
            style={{ left: "42.1%", top: "25.9%", width: "28.3%", height: "47.7%" }}
          >
            <button
              type="button"
              data-testid="hero-demo-play"
              onClick={onTogglePlay}
              aria-label={playing ? `Pause ${homeDemo.title}` : `Play ${homeDemo.title}`}
              className="pointer-events-auto absolute left-1/2 top-[56%] z-10 flex w-[94%] -translate-x-1/2 -translate-y-1/2 items-center gap-2.5 rounded-full border border-white/25 bg-white/12 px-3 py-2 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.14)] backdrop-blur-md transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            >
              <span className="relative grid h-10 w-10 shrink-0 place-items-center">
                {!playing && (
                  <>
                    <span className="hero-play-ripple" />
                    <span className="hero-play-ripple" style={{ animationDelay: "0.7s" }} />
                    <span className="hero-play-ripple" style={{ animationDelay: "1.4s" }} />
                  </>
                )}
                <span className="relative z-[1] grid h-10 w-10 place-items-center rounded-full bg-white text-slate-900 shadow-md">
                  {playing
                    ? <Pause className="h-4.5 w-4.5" fill="currentColor" />
                    : <Play className="h-4.5 w-4.5 translate-x-px" fill="currentColor" />}
                </span>
              </span>
              <span className="min-w-0 flex-1 py-0.5">
                <span className="block truncate text-[11px] font-semibold leading-tight tracking-wide" data-testid="hero-demo-title">
                  {homeDemo.title}
                </span>
                <span className="block text-[10px] leading-tight text-white/65">
                  {playing ? "Playing demo" : "Tap to listen"}
                </span>
              </span>
              {playing && (
                <span className="flex h-5 items-end gap-0.5 pr-0.5" aria-hidden>
                  {[4, 7, 5, 8, 4].map((h, i) => (
                    <span
                      key={i}
                      className="hero-wave w-0.5 rounded-full bg-white/80"
                      style={{ height: h, animationDelay: `${i * 0.1}s` }}
                    />
                  ))}
                </span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const FAQ_BASE = [
  // Getting started
  { q: "Do I need to write any code?", a: "No. Everything — greetings, branching, DTMF, silence handling and agent transfer — is built visually in the Campaign Designer." },
  { q: "How quickly can I launch my first campaign?", a: (name) => `Most teams go live the same day: design a flow, connect a VICIdial / SIP source, pick an AI engine, and start. ${name} handles the rest of the call path.` },
  { q: "Who can use the platform?", a: "Admins manage the fleet and pricing, resellers run white-label portals for their customers, and customers design campaigns and buy concurrent channels from their cash balance." },

  // AI agent
  { q: "What is an AI Agent?", a: (name) => `An AI Agent is a live voice conversation node in your flow. It listens to the caller, responds with natural speech (via your chosen LLM + TTS/STT or Gemini Live), follows your instructions, and can branch, collect data, or transfer — all inside ${name}.` },
  { q: "How do I create an AI Agent?", a: "Open the Campaign Designer and add an AI Agent node (or use the AI Agent Wizard). Set the product name, role, personality, language, and opening line, then connect success / not-interested / transfer paths like any other node." },
  { q: "Can the AI Agent use my product knowledge?", a: "Yes. Attach a Knowledge Base with FAQs, scripts, or product facts. The agent answers from that content instead of inventing details, which keeps replies on-brand and accurate." },
  { q: "Which languages and voices can the AI Agent use?", a: "Pick the language and voice per agent (and per campaign). Outbound speech uses your configured TTS or a low-latency live model; inbound speech is transcribed with your STT engine so intent matching stays reliable." },
  { q: "Can the AI Agent transfer to a live human?", a: (name) => `Yes. When the flow hits a Transfer node (or the agent decides to hand off), ${name} bridges the live call to your agent over SIP/PSTN — including optional SIP headers from fields you collected earlier.` },
  { q: "What happens if the caller is silent or says “not interested”?", a: "Silence and intent rules are part of the flow. You can re-prompt, hang up, schedule a callback, or transfer — including phrase matching for languages like Urdu — so the agent does not get stuck or hang up too early." },
  { q: "Does the AI Agent detect answering machines?", a: "Yes. Built-in Answering-Machine Detection classifies human vs machine within about a second. You can hang up, leave a voicemail drop, or continue only for live humans." },

  // Telephony & ops
  { q: "How does the human hand-off work?", a: (name) => `When a node hits “Transfer to Agent”, ${name} bridges the live call to your agent over SIP/PSTN while preserving the conversation context.` },
  { q: "Can it detect voicemail?", a: "Yes. Built-in Answering-Machine Detection classifies human vs machine within about a second and routes the call accordingly." },
  { q: "Which telephony systems are supported?", a: "Inbound campaigns typically arrive via OpenSIPS from VICIdial (or other SIP trunks). FreeSWITCH handles media; AI engines run the conversation. Capacity-aware routing picks a healthy engine per call." },
  { q: "Can I test a flow before going live?", a: "Yes. Use Test Call from the Campaign Designer to run the same AI path in the browser (microphone), including Live model modes, without dialing a real number." },

  // Billing & security
  { q: "How is capacity billed?", a: "You buy concurrent-call channels from your cash balance (prorated), with monthly renewal. Admins and resellers manage pricing per account. Usage components (AI / TTS / STT) can also be priced per minute." },
  { q: "Can resellers white-label the portal?", a: "Yes. Resellers can activate white labeling with their own product name, domain, logo, and favicon. Visitors on that domain see the reseller brand; the main platform brand stays on the primary site." },
  { q: "Is my data isolated from other tenants?", a: "Yes. Every account is a tenant with strict isolation — campaigns, CDRs, wallets, and servers are scoped so one customer cannot see another’s data." },
];

export default function Landing() {
  const productName = useProductName();
  const [introVideos, setIntroVideos] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);
  const [demoCalls, setDemoCalls] = useState([]);
  const [homeDemo, setHomeDemo] = useState(null);
  const [heroPlaying, setHeroPlaying] = useState(false);
  const [listPlayingId, setListPlayingId] = useState(null);
  const [customerPricing, setCustomerPricing] = useState(null);
  // Intro videos stay on the main platform site; AI demo recordings + pricing show on reseller WL too.
  const showIntroVideos = isPlatformHost();
  const showPricing = Boolean(
    customerPricing && (
      customerPricing.price_per_channel_minor != null
      || customerPricing.ai_agent_per_min_minor != null
      || customerPricing.tts_per_min_minor != null
    ),
  );

  const nameOrPlatform = productName || "our platform";
  const FAQ = FAQ_BASE.map((item) => ({
    ...item,
    a: typeof item.a === "function" ? item.a(nameOrPlatform) : item.a,
  }));

  useEffect(() => {
    let injected = [];
    let cancelled = false;
    api.get("/settings/public").then(({ data }) => {
      if (cancelled) return;
      const snippet = (data?.chat_widget_script || "").trim();
      if (!snippet) return;
      const tmpl = document.createElement("template");
      tmpl.innerHTML = snippet;
      Array.from(tmpl.content.childNodes).forEach((node) => {
        if (node.nodeName === "SCRIPT") {
          const s = document.createElement("script");
          for (const attr of node.attributes) s.setAttribute(attr.name, attr.value);
          s.text = node.textContent || "";
          document.body.appendChild(s);
          injected.push(s);
        } else if (node.nodeType === 1) {
          document.body.appendChild(node);
          injected.push(node);
        }
      });
    }).catch(() => {});
    return () => {
      cancelled = true;
      injected.forEach((n) => n.remove());
      try { window.Tawk_API?.hideWidget?.(); } catch { /* noop */ }
      document.querySelectorAll('iframe[src*="tawk.to"], #tawkchat-container, .widget-visible')
        .forEach((el) => el.remove());
    };
  }, []);

  useEffect(() => {
    api.get("/settings/public")
      .then(({ data }) => {
        setDemoCalls(data?.demo_calls || []);
        setHomeDemo(data?.home_demo_call || null);
        if (showIntroVideos) {
          setIntroVideos(data?.intro_videos || []);
        } else {
          setIntroVideos([]);
        }
        setCustomerPricing(data?.customer_pricing || null);
      })
      .catch(() => {});
  }, [showIntroVideos]);

  useEffect(() => {
    const el = document.getElementById("hero-demo-audio");
    if (!el) return;
    if (heroPlaying) {
      el.play().catch(() => setHeroPlaying(false));
    } else {
      el.pause();
    }
  }, [heroPlaying, homeDemo?.id]);

  const toggleHeroPlay = () => {
    if (!homeDemo) return;
    setListPlayingId(null);
    setHeroPlaying((p) => !p);
  };

  const toggleListPlay = (id) => {
    setHeroPlaying(false);
    setListPlayingId((cur) => (cur === id ? null : id));
  };

  return (
    <MarketingShell active="home">
      <style>{`
        @keyframes dash{to{stroke-dashoffset:-24}}
        .flow-edge{stroke-dasharray:6 6;animation:dash 1.1s linear infinite}
        @keyframes nodein{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        .flow-node{animation:nodein .5s ease both;transform-box:fill-box;transform-origin:center}
      `}</style>

      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute -top-40 left-1/2 h-[900px] w-[900px] -translate-x-1/2 rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.16), transparent 60%)" }} />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-6 pb-14 pt-20 lg:grid-cols-2 lg:gap-12">
          <div className="m-rise max-w-3xl">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
              AI Telephony Control Plane
            </span>
            <h1 className="disp mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              AI voice agents that call, qualify,<br className="hidden sm:block" /> and transfer to your team.
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
              {nameOrPlatform === "our platform" && !productName ? (
                <span className="inline-block h-5 w-full max-w-xl rounded bg-muted animate-pulse" aria-hidden />
              ) : (
                <>
                  {productName || nameOrPlatform} runs human-like outbound &amp; inbound phone conversations at scale. Design the
                  call flow visually, let the bot handle the talking, and warm-transfer hot leads straight
                  to a live agent — all on your own SIP infrastructure.
                </>
              )}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup" data-testid="hero-get-started"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
                Get started <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#ai-recordings"
                data-testid="hero-ai-recordings"
                className="inline-flex items-center gap-2 rounded-xl border border-border px-6 py-3.5 font-medium text-foreground hover:bg-muted"
              >
                Listen to AI Bot Recorded Calls
              </a>
              {homeDemo && (
                <button
                  type="button"
                  onClick={toggleHeroPlay}
                  data-testid="hero-demo-play-mobile"
                  className="inline-flex items-center gap-2 rounded-xl border border-border px-6 py-3.5 font-medium text-foreground hover:bg-muted md:hidden"
                >
                  {heroPlaying
                    ? <><Pause className="h-4 w-4" /> Pause demo</>
                    : <><Play className="h-4 w-4" /> Play demo</>}
                </button>
              )}
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              {[
                { Icon: Zap, c: C.amber, t: "Sub-second responses" },
                { Icon: ShieldCheck, c: C.emerald, t: "AMD & human transfer" },
                { Icon: PhoneOutgoing, c: C.blue, t: "Thousands of concurrent calls" },
              ].map(({ Icon, c, t }) => (
                <span key={t} className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3.5 py-1.5 text-sm text-muted-foreground">
                  <Icon className="h-4 w-4" style={{ color: c }} /> {t}
                </span>
              ))}
            </div>
          </div>
          <div className="m-rise hidden md:block" style={{ animationDelay: "0.12s" }}>
            <HeroVisual
              homeDemo={homeDemo}
              playing={heroPlaying}
              onTogglePlay={toggleHeroPlay}
            />
          </div>
        </div>
        {homeDemo && (
          <audio
            id="hero-demo-audio"
            key={homeDemo.id}
            src={`${API}/settings/demo-calls/${homeDemo.id}/audio`}
            preload="none"
            onEnded={() => setHeroPlaying(false)}
            data-testid="hero-demo-audio"
          />
        )}
      </section>

      <section id="flow" className="mx-auto max-w-7xl px-6 py-16">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Visual Campaign Designer</span>
          <h2 className="disp mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Every call follows a flow you design</h2>
          <p className="mt-4 text-muted-foreground">
            This is a real {productName || "voice"} campaign: greet the prospect, listen, understand intent, then branch —
            connect the interested ones to an agent, thank the rest, and log everything.
          </p>
        </div>
        <div className="mt-10">
          <FlowDiagram />
        </div>
      </section>

      <section id="ai-recordings" className="relative scroll-mt-20 border-y border-border bg-muted/30" data-testid="landing-ai-recordings">
          <style>{`
            @keyframes rec-ripple {
              0% { transform: scale(1); opacity: 0.45; }
              70% { transform: scale(1.75); opacity: 0; }
              100% { transform: scale(1.75); opacity: 0; }
            }
            @keyframes rec-wave {
              0%,100% { transform: scaleY(0.4); }
              50% { transform: scaleY(1); }
            }
            .rec-ripple {
              position: absolute; inset: 0; border-radius: 9999px;
              border: 2px solid hsl(var(--primary) / 0.45);
              animation: rec-ripple 2s ease-out infinite;
              pointer-events: none;
            }
            .rec-wave-bar {
              transform-origin: center bottom;
              animation: rec-wave 1s ease-in-out infinite;
            }
          `}</style>
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            <div
              className="absolute -right-24 top-10 h-72 w-72 rounded-full blur-3xl"
              style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.12), transparent 70%)" }}
            />
          </div>
          <div className="relative mx-auto max-w-7xl px-6 py-16">
            <div className="max-w-2xl">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">AI Recordings</span>
              <h2 className="disp mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                Listen to AI demo calls
              </h2>
              <p className="mt-4 text-muted-foreground">
                Sample conversations from live AI agents — press play on any recording.
              </p>
            </div>

            {demoCalls.length === 0 ? (
              <p className="mt-8 text-sm text-muted-foreground" data-testid="ai-recordings-empty">
                Demo recordings will appear here once added in System Settings.
              </p>
            ) : (
              <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {demoCalls.map((d) => {
                  const playing = listPlayingId === d.id;
                  return (
                    <article
                      key={d.id}
                      data-testid={`ai-recording-${d.id}`}
                      className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-card p-5 transition-all duration-300 hover:-translate-y-0.5 ${
                        playing
                          ? "border-primary/40 shadow-[0_12px_40px_-16px_hsl(var(--primary)/0.45)]"
                          : "border-border hover:border-primary/25"
                      }`}
                    >
                      {d.home_page && (
                        <span className="absolute right-4 top-4 rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                          Featured
                        </span>
                      )}

                      <div className="flex items-start gap-4">
                        <button
                          type="button"
                          onClick={() => toggleListPlay(d.id)}
                          className="relative mt-0.5 grid h-14 w-14 shrink-0 place-items-center"
                          aria-label={playing ? `Pause ${d.title}` : `Play ${d.title}`}
                          data-testid={`ai-recording-play-${d.id}`}
                        >
                          {!playing && (
                            <>
                              <span className="rec-ripple" />
                              <span className="rec-ripple" style={{ animationDelay: "0.65s" }} />
                            </>
                          )}
                          <span className={`relative z-[1] grid h-14 w-14 place-items-center rounded-full text-primary-foreground shadow-md transition-transform duration-300 group-hover:scale-105 ${
                            playing ? "bg-primary" : "bg-primary"
                          }`}>
                            {playing
                              ? <Pause className="h-6 w-6" fill="currentColor" />
                              : <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" />}
                          </span>
                        </button>

                        <div className="min-w-0 flex-1 pt-1">
                          <h3 className="disp truncate text-lg font-semibold leading-snug pr-16 sm:pr-0">
                            {d.title}
                          </h3>
                          <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
                            <Headphones className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">
                              {d.details?.trim()
                                || (d.home_page ? "Featured on home page" : "AI demo call")}
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="mt-5 flex items-end gap-1.5 h-8" aria-hidden>
                        {(playing
                          ? [10, 18, 14, 22, 12, 20, 16, 24, 11, 19, 15, 21]
                          : [8, 12, 10, 14, 9, 13, 11, 15, 8, 12, 10, 13]
                        ).map((h, i) => (
                          <span
                            key={i}
                            className={`w-1 flex-1 rounded-full bg-primary/25 ${playing ? "rec-wave-bar bg-primary/70" : ""}`}
                            style={{
                              height: h,
                              animationDelay: playing ? `${i * 0.07}s` : undefined,
                            }}
                          />
                        ))}
                      </div>

                      {playing && (
                        <audio
                          key={`list-${d.id}`}
                          src={`${API}/settings/demo-calls/${d.id}/audio`}
                          autoPlay
                          onEnded={() => setListPlayingId(null)}
                          className="mt-4 w-full h-9"
                          controls
                          data-testid={`ai-recording-audio-${d.id}`}
                        />
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
      </section>

      {showIntroVideos && introVideos.length > 0 && (
        <section id="intro-videos" className="mx-auto max-w-7xl scroll-mt-20 px-6 py-16" data-testid="landing-intro-videos">
          <div className="max-w-2xl">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Watch &amp; learn</span>
            <h2 className="disp mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              See {productName || "it"} in action
            </h2>
            <p className="mt-4 text-muted-foreground">Quick walkthroughs to get you configured and integrated fast.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            {introVideos.map((v) => (
              <button
                key={v.id}
                data-testid={`intro-video-card-${v.id}`}
                onClick={() => setActiveVideo(v)}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card text-left transition-all duration-300 hover:-translate-y-0.5"
              >
                <div className="relative aspect-video bg-muted">
                  {v.has_cover ? (
                    <img src={`${API}/settings/intro-videos/${v.id}/cover`} alt={v.title}
                      className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100" />
                  ) : (
                    <div className="grid h-full w-full place-items-center text-muted-foreground"><Play className="h-10 w-10" /></div>
                  )}
                  <span className="absolute inset-0 grid place-items-center">
                    <span className="grid h-16 w-16 place-items-center rounded-full bg-primary/90 text-primary-foreground backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
                      <Play className="h-7 w-7 translate-x-0.5" fill="currentColor" />
                    </span>
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="disp text-lg font-semibold">{v.title}</h3>
                  <p className="mt-1 inline-flex items-center gap-1 text-sm text-muted-foreground">Play video <ArrowRight className="h-4 w-4" /></p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      <Dialog open={!!activeVideo} onOpenChange={(o) => !o && setActiveVideo(null)}>
        <DialogContent className="max-w-3xl overflow-hidden border-border bg-card p-0" data-testid="intro-video-modal">
          <DialogHeader className="px-5 pt-4">
            <DialogTitle>{activeVideo?.title}</DialogTitle>
          </DialogHeader>
          <div className="p-4 pt-2">
            {activeVideo && (
              <video key={activeVideo.id} src={`${API}/settings/intro-videos/${activeVideo.id}/video`}
                controls autoPlay playsInline className="w-full rounded-lg bg-black" data-testid="intro-video-player" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <section id="features" className="border-y border-border bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Platform</span>
              <h2 className="disp mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Everything you need to run voice at scale</h2>
            </div>
            <Link to="/features" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
              See all features <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title}
                data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className="rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1">
                <div className="mb-4 grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
                  <f.icon className="h-5 w-5" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{f.badge}</p>
                <h3 className="disp mt-1.5 text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="mx-auto max-w-7xl px-6 py-16">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">How it works</span>
          <h2 className="disp mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Live in three steps</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-2xl border border-border bg-card p-7">
              <p className="disp text-5xl font-extrabold text-primary/30">{s.n}</p>
              <h3 className="disp mt-3 text-xl font-semibold">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {showPricing && customerPricing && (
        <section id="pricing" className="scroll-mt-20 border-y border-border bg-muted/30" data-testid="landing-pricing">
          <div className="mx-auto max-w-7xl px-6 py-16">
            <div className="mx-auto max-w-2xl text-center">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Pricing</span>
              <h2 className="disp mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Simple customer rates</h2>
              <p className="mt-4 text-muted-foreground">
                {customerPricing?.source === "reseller"
                  ? "Standard list prices for new customer accounts on this portal."
                  : "Standard list prices for new customer accounts. Your admin can customize rates when you sign up."}
              </p>
            </div>
            <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                {
                  key: "channel",
                  label: "Price per Channel",
                  value: customerPricing.price_per_channel_minor,
                  unit: "/ channel / mo",
                  hint: "Concurrent call capacity",
                  Icon: Gauge,
                },
                {
                  key: "ai",
                  label: "AI Agent / min",
                  value: customerPricing.ai_agent_per_min_minor,
                  unit: "/ min",
                  hint: "Live AI conversation time",
                  Icon: Bot,
                },
                {
                  key: "tts",
                  label: "TTS / min",
                  value: customerPricing.tts_per_min_minor,
                  unit: "/ min",
                  hint: "Text-to-speech playback",
                  Icon: Volume2,
                },
              ].map(({ key, label, value, unit, hint, Icon }) => (
                <div
                  key={key}
                  data-testid={`landing-price-${key}`}
                  className="rounded-2xl border border-border bg-card p-6 text-center transition-transform hover:-translate-y-0.5"
                >
                  <div className="mx-auto grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="mt-4 text-sm font-medium text-muted-foreground">{label}</p>
                  <p className="disp mt-2 text-3xl font-extrabold tracking-tight tabular-nums">
                    {value == null ? "—" : money(value, customerPricing.currency)}
                  </p>
                  {value != null && (
                    <p className="mt-1 text-xs text-muted-foreground">{unit}</p>
                  )}
                  <p className="mt-3 text-[11px] text-muted-foreground">{hint}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link
                to="/signup"
                data-testid="landing-pricing-cta"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Get started <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <section id="faq" className="scroll-mt-20 border-t border-border bg-muted/20" data-testid="landing-faq">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">FAQ</span>
            <h2 className="disp mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Questions, answered</h2>
            <p className="mt-3 text-sm text-muted-foreground sm:text-base">
              Platform basics, AI Agents, telephony, and billing — in plain language.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
            {[FAQ.slice(0, Math.ceil(FAQ.length / 2)), FAQ.slice(Math.ceil(FAQ.length / 2))].map((col, colIdx) => (
              <div key={colIdx} className="flex flex-col gap-3.5">
                {col.map((f, i) => {
                  const idx = colIdx === 0 ? i : Math.ceil(FAQ.length / 2) + i;
                  return (
                    <details
                      key={idx}
                      data-testid={`faq-${idx}`}
                      className="group rounded-2xl border border-border/80 bg-card px-5 py-4 shadow-sm transition-[background,box-shadow] open:border-primary/25 open:bg-card open:shadow-md"
                    >
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium leading-snug text-foreground [&::-webkit-details-marker]:hidden">
                        <span>{f.q}</span>
                        <span
                          aria-hidden
                          className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border bg-muted/50 text-lg leading-none text-muted-foreground transition-all group-open:rotate-45 group-open:border-primary/30 group-open:bg-primary/10 group-open:text-primary"
                        >
                          +
                        </span>
                      </summary>
                      <p className="mt-3 border-t border-border/60 pt-3 text-sm leading-relaxed text-muted-foreground">
                        {f.a}
                      </p>
                    </details>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-8">
        <div className="relative overflow-hidden rounded-3xl border border-border px-10 py-14 text-center sm:px-16"
          style={{ background: "linear-gradient(135deg, hsl(var(--primary) / 0.14), hsl(var(--muted) / 0.4))" }}>
          <Rocket className="mx-auto mb-4 h-8 w-8 text-primary" />
          <h2 className="disp text-3xl font-bold tracking-tight sm:text-4xl">Ready to launch your first AI voice campaign?</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">Sign in to the control plane and put a whole team of voice agents on the phones today.</p>
          <Link to="/signup" data-testid="cta-signin"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3.5 font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
            Create your account <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Check className="h-3.5 w-3.5 text-emerald-500" /> Admin, reseller &amp; customer access
          </p>
        </div>
      </section>
    </MarketingShell>
  );
}
